import { PoMultiselectOption } from '@po-ui/ng-components';
import { resourceTypeLabel as sharedResourceTypeLabel } from '../../../core/utils/resource-type-label.util';

import { CostBucket } from '../interfaces/costs.interface';

/**
 * Chave que o backend manda quando área/usuário (`requested_by`/`area_id`) não foi
 * capturado na execução — não é erro de leitura, o dado nasceu vazio.
 */
const UNASSIGNED_KEY = '(sem informação)';

/**
 * Rótulo de exibição da chave de uma dimensão (área/usuário). Só troca a chave
 * especial de dado ausente por um texto neutro — `(outros N)` e as demais chaves
 * passam direto, sem essa função inventar nada sobre elas.
 */
export function bucketKeyLabel(key: string): string {
  return key === UNASSIGNED_KEY ? 'Não classificado' : key;
}

/** Chave da fatia em que o backend soma a cauda quando a dimensão passa do teto. */
const AGGREGATE_TAIL_PATTERN = /^\(outros \d+\)$/;

/**
 * True quando a chave é a cauda agregada. É o único sinal confiável de que o teto
 * entrou: comparar a contagem de linhas com `breakdownKeys` erra quando a cauda tem
 * uma chave só (201 chaves viram 200 linhas + 1 agregada = 201 linhas, e a
 * comparação daria "não cortado" justamente com a linha agregada na tela).
 */
export function isAggregateTailKey(key: string | undefined | null): boolean {
  return AGGREGATE_TAIL_PATTERN.test((key ?? '').trim());
}

/** Rótulo de um tipo de review; tipo desconhecido volta como veio. */
export const resourceTypeLabel = sharedResourceTypeLabel;


/**
 * Os tipos que o usuário reconhece e escolhe no filtro — sem as variantes
 * `-upload`, que são detalhe interno da coluna `resource_type`: o backend já expande
 * "comparison"/"standalone" para cobrir upload, então a UI não precisa listá-las.
 */
export const RESOURCE_TYPE_FILTER_OPTIONS: PoMultiselectOption[] = [
  { value: 'changeset', label: 'Changeset' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'issue', label: 'Issue' },
  { value: 'standalone', label: 'Standalone' },
  { value: 'pull', label: 'Pull Request' },
  { value: 'commit', label: 'Commit' },
  { value: 'git-comparison', label: 'Git Comparison' }
];

/**
 * Quebra a chave da dimensão `resource` (`changeset:604564`) em tipo e identificador.
 *
 * Divide no PRIMEIRO `:` apenas: identificadores de comparação e standalone contêm
 * underscores e hifens, e um dia podem conter `:` — o tipo é sempre o primeiro
 * segmento, o resto é o identificador inteiro.
 *
 * Chaves especiais (`(sem informação)`, `(outros N)`) não têm tipo: são agregados,
 * não reviews, e inventar um tipo para elas seria mentir sobre o que representam.
 */
export function splitResourceKey(key: string): { type?: string; id: string } {
  if (!key || key.startsWith('(')) { return { id: key }; }

  const separator = key.indexOf(':');
  if (separator < 0) { return { id: key }; }

  return {
    type: resourceTypeLabel(key.slice(0, separator)),
    id: key.slice(separator + 1)
  };
}

/** Como uma fatia do breakdown é exibida: rótulo da chave e, na dimensão `resource`, o tipo. */
export interface BucketDisplay {
  key: string;
  /** Só na dimensão `resource`: o tipo do review, separado do identificador. */
  type?: string;
}

/**
 * Resolve o rótulo de uma fatia do breakdown — a mesma regra para o gráfico, a tabela
 * e o CSV.
 *
 * Ficava duplicada em três lugares (barra, tabela e agora a exportação), e cada cópia
 * divergia num detalhe: a barra não traduzia `(sem informação)`, a tabela traduzia. Um
 * ponto só garante que a mesma chave apareça com o mesmo texto nas três saídas — que é
 * o requisito, já que o usuário compara a barra com a tabela e a tabela com a planilha.
 */
export function resolveBucketDisplay(bucket: CostBucket, splitResource = false): BucketDisplay {
  if (!splitResource) { return { key: bucketKeyLabel(bucket.key) }; }

  // O backend manda o tipo pronto em `resourceType`; o corte manual no primeiro ":"
  // é só o fallback (chave agregada, que nunca tem tipo, ou resposta de uma versão
  // anterior da API) — e erraria num id que contivesse ":".
  const fallback = splitResourceKey(bucket.key);

  // O `resourceType` só serve para cortar a chave quando ela REALMENTE começa por ele.
  // Sem essa checagem, um bucket agregado que viesse carimbado com tipo — `(outros 1232)`
  // com `resourceType: 'comparison'` — teria os 11 primeiros caracteres cortados em
  // silêncio, e o fallback documentado aqui embaixo nunca seria alcançado.
  const prefix = bucket.resourceType ? `${bucket.resourceType}:` : '';
  const prefixed = !!prefix && bucket.key.startsWith(prefix);

  const type = bucket.resourceType ? resourceTypeLabel(bucket.resourceType) : fallback.type;
  const id = prefixed ? bucket.key.slice(prefix.length) : fallback.id;

  // `label` só vem para comparison/standalone, cujo id é opaco (path, uuid), e já chega
  // carimbado "Origem excluída (id)" quando a origem foi apagada.
  return { key: (bucket.label || bucketKeyLabel(id)), type };
}
